import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ArrowLeft,
  ArrowRight,
  AlertTriangle,
  DollarSign,
  TrendingUp,
  Home,
  ExternalLink,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { ProTag } from '../components/ProUpgrade';

import { API } from '../lib/api';

function formatDate(value) {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '--';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatMoney(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return '$0';
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function scoreColor(score) {
  if (score === null || score === undefined) return 'text-ink-muted';
  if (score <= 40) return 'text-sev-low';
  if (score <= 60) return 'text-sev-med';
  return 'text-sev-high';
}

function deltaBadge(delta) {
  if (delta === null || delta === undefined) return null;
  const up = delta > 0;
  const flat = delta === 0;
  return (
    <span className={`text-xs font-mono px-2 py-0.5 rounded border ${
      flat ? 'bg-sunken text-ink-muted border-line':
      up ? 'bg-sev-high-soft text-sev-high border-sev-high':
      'bg-sev-low-soft text-sev-low border-sev-low'
    }`}>
      {up ? '+' : ''}{delta}
    </span>
  );
}

export default function TurnoverComparisonPage() {
  const { id: propertyId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [moveInId, setMoveInId] = useState('');
  const [moveOutId, setMoveOutId] = useState('');
  const [needsPro, setNeedsPro] = useState(false);

  const fetchComparison = useCallback(async (miId, moId) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (miId) params.set('move_in_id', miId);
      if (moId) params.set('move_out_id', moId);
      const qs = params.toString();
      const url = `${API}/properties/${propertyId}/turnover-comparison${qs ? `?${qs}` : ''}`;
      const res = await axios.get(url);
      setData(res.data);
      if (res.data.move_in?.id) setMoveInId(res.data.move_in.id);
      if (res.data.move_out?.id) setMoveOutId(res.data.move_out.id);
    } catch (err) {
      if (err.response?.status === 402) setNeedsPro(true);
      else toast.error("Couldn't load the comparison. Refresh to try again.");
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    fetchComparison('', '');
  }, [fetchComparison]);

  const applySelection = () => {
    if (!moveInId || !moveOutId) {
      toast.error('Select both a move-in and move-out inspection');
      return;
    }
    fetchComparison(moveInId, moveOutId);
  };

  if (needsPro) {
    return (
      <DashboardLayout>
        <div className="flex max-w-[640px] flex-col items-start gap-4 rounded-md border border-line bg-surface p-8" data-testid="comparison-pro-gate">
          <Link to={`/properties/${propertyId}`} className="inline-flex items-center gap-1.5 text-sm font-bold text-ink-muted no-underline hover:text-ink">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back to property
          </Link>
          <ProTag />
          <h1 className="m-0 font-display text-3xl font-bold leading-9">Move-in and move-out comparison</h1>
          <p className="m-0 text-ink-muted">
            Line up the same rooms across inspections so new damage stands apart from what was already there.
            It's part of Pilot Pro, a one-time $15 with no subscription.
          </p>
          <Button asChild><Link to="/upgrade">See Pilot Pro</Link></Button>
        </div>
      </DashboardLayout>
    );
  }

  if (loading && !data) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-sunken rounded w-48" />
          <div className="h-32 bg-sunken rounded-md" />
          <div className="h-64 bg-sunken rounded-md" />
        </div>
      </DashboardLayout>
    );
  }

  const property = data?.property;
  const summary = data?.summary;
  const ready = data?.ready;

  return (
    <DashboardLayout>
      <div data-testid="turnover-comparison-page">
        <Link
          to={`/properties/${propertyId}`}
          className="inline-flex items-center gap-2 text-ink-muted hover:text-ink mb-6 transition-colors text-sm"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Property
        </Link>

        <div className="border border-line rounded-md bg-surface p-6 mb-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-md bg-marker-soft flex items-center justify-center">
                <Home className="w-6 h-6 text-ink" strokeWidth={1.5} />
              </div>
              <div>
                <h1 className="text-xl font-semibold text-ink font-display">
                  Turnover Comparison
                </h1>
                <p className="text-sm text-ink-muted mt-0.5">{property?.name} · {property?.address}</p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mt-6">
            <div className="space-y-2">
              <label className="text-xs font-medium text-ink-muted uppercase tracking-[0.08em]">Move-In Baseline</label>
              <Select value={moveInId} onValueChange={setMoveInId}>
                <SelectTrigger className="bg-sunken border-line h-11" data-testid="move-in-select">
                  <SelectValue placeholder="Select move-in inspection" />
                </SelectTrigger>
                <SelectContent>
                  {(data?.available?.move_in || []).map((insp) => (
                    <SelectItem key={insp.id} value={insp.id}>
                      {formatDate(insp.created_at)} · Score {insp.overall_score ?? '—'} · {insp.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {data?.move_in && (
                <Link
                  to={`/inspections/${data.move_in.id}`}
                  className="inline-flex items-center gap-1 text-xs text-ink hover:text-ink"
                >
                  View inspection <ExternalLink className="w-3 h-3" />
                </Link>
              )}
            </div>
            <div className="space-y-2">
              <label className="text-xs font-medium text-ink-muted uppercase tracking-[0.08em]">Move-Out Final</label>
              <Select value={moveOutId} onValueChange={setMoveOutId}>
                <SelectTrigger className="bg-sunken border-line h-11" data-testid="move-out-select">
                  <SelectValue placeholder="Select move-out inspection" />
                </SelectTrigger>
                <SelectContent>
                  {(data?.available?.move_out || []).map((insp) => (
                    <SelectItem key={insp.id} value={insp.id}>
                      {formatDate(insp.created_at)} · Score {insp.overall_score ?? '—'} · {insp.status}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {data?.move_out && (
                <Link
                  to={`/inspections/${data.move_out.id}`}
                  className="inline-flex items-center gap-1 text-xs text-ink hover:text-ink"
                >
                  View inspection <ExternalLink className="w-3 h-3" />
                </Link>
              )}
            </div>
          </div>

          <Button
            onClick={applySelection}
            disabled={loading || !moveInId || !moveOutId}
            className="mt-4 bg-marker hover:bg-marker-hover text-on-marker"
            data-testid="compare-btn"
          >
            {loading ? 'Loading...' : 'Compare Inspections'}
          </Button>
        </div>

        {!ready && (
          <div className="border border-sev-med bg-sev-med-soft rounded-md p-6 text-center">
            <AlertTriangle className="w-8 h-8 text-sev-med mx-auto mb-3" />
            <p className="text-sm text-sev-med font-medium">{data?.message}</p>
            <p className="text-xs text-sev-med mt-2">
              Create a move-in inspection when the tenant arrives, then a move-out when they leave.
            </p>
            <div className="flex gap-3 justify-center mt-4">
              <Link to={`/inspections?property=${propertyId}`}>
                <Button variant="outline" size="sm">Go to Inspections</Button>
              </Link>
            </div>
          </div>
        )}

        {ready && summary && (
          <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="border border-line rounded-md bg-surface p-4">
                <div className="flex items-center gap-2 text-ink-muted text-xs uppercase tracking-[0.08em] mb-2">
                  <TrendingUp className="w-3.5 h-3.5" /> Damage Score Change
                </div>
                <div className="flex items-baseline gap-2">
                  <span className={`text-2xl font-bold font-mono ${scoreColor(summary.move_out_score)}`}>
                    {summary.move_out_score ?? '—'}
                  </span>
                  <ArrowRight className="w-4 h-4 text-ink-muted" />
                  <span className={`text-lg font-mono ${scoreColor(summary.move_in_score)}`}>
                    {summary.move_in_score ?? '—'}
                  </span>
                </div>
                <div className="mt-2">{deltaBadge(summary.score_delta)}</div>
              </div>

              <div className="border border-line rounded-md bg-surface p-4">
                <div className="flex items-center gap-2 text-ink-muted text-xs uppercase tracking-[0.08em] mb-2">
                  <DollarSign className="w-3.5 h-3.5" /> Net Tenant Charge
                </div>
                <p className="text-2xl font-bold font-mono text-sev-high" data-testid="net-charge">
                  {formatMoney(summary.net_tenant_charge)}
                </p>
                <p className="text-xs text-ink-muted mt-1">New chargeable damage since move-in</p>
              </div>

              <div className="border border-line rounded-md bg-surface p-4">
                <div className="text-ink-muted text-xs uppercase tracking-[0.08em] mb-2">Move-Out Total</div>
                <p className="text-2xl font-bold font-mono text-ink">
                  {formatMoney(summary.move_out_chargeable)}
                </p>
                <p className="text-xs text-ink-muted mt-1">
                  Move-in baseline: {formatMoney(summary.move_in_chargeable)}
                </p>
              </div>

              <div className="border border-line rounded-md bg-surface p-4">
                <div className="text-ink-muted text-xs uppercase tracking-[0.08em] mb-2">New Damage Items</div>
                <p className="text-2xl font-bold font-mono text-sev-med">
                  {summary.new_damage_count}
                </p>
                <p className="text-xs text-ink-muted mt-1">Across {summary.rooms_compared} rooms</p>
              </div>
            </div>

            <div className="border border-line rounded-md overflow-hidden bg-surface">
              <div className="bg-sunken px-5 py-4 border-b border-line">
                <h2 className="text-sm font-semibold text-ink">Room-by-Room Delta</h2>
              </div>

              {(data.rooms || []).length === 0 ? (
                <div className="p-8 text-center text-sm text-ink-muted">
                  No room analyses yet. Upload photos in both inspections to compare.
                </div>
              ) : (
                <div className="divide-y divide-line">
                  {data.rooms.map((room) => (
                    <div key={room.room_name} className="px-5 py-4" data-testid={`room-compare-${room.room_name}`}>
                      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                        <div>
                          <h3 className="text-sm font-semibold text-ink">{room.room_name}</h3>
                          {room.only_in_move_in && (
                            <span className="text-xs text-sev-med">Not re-inspected at move-out</span>
                          )}
                          {room.only_in_move_out && (
                            <span className="text-xs text-ink">New room documented at move-out</span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <div className="text-center">
                            <p className="text-xs text-ink-muted uppercase">Move-In</p>
                            <p className={`font-mono font-semibold ${scoreColor(room.move_in_score)}`}>
                              {room.move_in_score ?? '—'}
                            </p>
                          </div>
                          <ArrowRight className="w-4 h-4 text-ink-muted" />
                          <div className="text-center">
                            <p className="text-xs text-ink-muted uppercase">Move-Out</p>
                            <p className={`font-mono font-semibold ${scoreColor(room.move_out_score)}`}>
                              {room.move_out_score ?? '—'}
                            </p>
                          </div>
                          {deltaBadge(room.score_delta)}
                          {room.net_new_chargeable > 0 && (
                            <span className="text-xs font-mono bg-sev-high-soft text-sev-high border border-sev-high px-2 py-1 rounded">
                              +{formatMoney(room.net_new_chargeable)}
                            </span>
                          )}
                        </div>
                      </div>

                      {room.new_damages?.length > 0 ? (
                        <div className="bg-sev-high-soft border border-sev-high rounded-md overflow-hidden">
                          <div className="px-3 py-2 bg-sev-high-soft border-b border-sev-high">
                            <p className="text-xs font-medium text-sev-high">
                              {room.new_damage_count} new damage item{room.new_damage_count !== 1 ? 's' : ''} since move-in
                            </p>
                          </div>
                          <ul className="divide-y divide-sev-high">
                            {room.new_damages.map((d, i) => (
                              <li key={i} className="px-3 py-2 flex items-start justify-between gap-3 text-sm">
                                <div>
                                  <span className="font-medium text-ink capitalize">
                                    {(d.type || 'damage').replace(/_/g, ' ')}
                                  </span>
                                  {d.location && (
                                    <span className="text-ink-muted"> · {d.location}</span>
                                  )}
                                  {d.description && (
                                    <p className="text-xs text-ink-muted mt-0.5">{d.description}</p>
                                  )}
                                </div>
                                <div className="text-right shrink-0">
                                  <p className="font-mono font-semibold text-ink">{formatMoney(d.cost)}</p>
                                  <p className={`text-xs uppercase ${d.chargeable ? 'text-sev-high': 'text-ink-muted'}`}>
                                    {d.chargeable ? 'Tenant' : 'Wear'}
                                  </p>
                                </div>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ) : (
                        <p className="text-xs text-sev-low">No new damage since move-in baseline</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
